<script setup>
import AdminLayout from '@/layouts/AdminLayout.vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { organizationRolesApi } from '@/api/Roles/OrganizationRolesApi.js'
import router from '@/router/router.js'
import OrganizationRoleEditCard from '@/views/Admin/Roles/OrganizationRoleEditCard.vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import InputText from '@/components/Forms/InputText.vue'
import { InputIcon } from '@/models/enums/InputIcon.js'
import { Toaster } from '@/models/utils/Toaster.js'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import { useAuthStore } from '@/stores/authStore.js'
import LoadingSpinner from '@/components/Spinners/LoadingSpinner.vue'
import { onBeforeRouteLeave } from 'vue-router'
import { useBroadcastChannel } from '@vueuse/core'

/**
 * Permissions returned in the request and used to create each card. This should be updated when new permissions are added.
 * @type {string[]}
 */
const permission_list = [
	{ 'name': 'admin_permissions', 'routes': [] },
	{ 'name': 'data_permissions', 'routes': ['data.data-submissions', 'data.submit-data'] },
	{ 'name': 'resource_permissions', 'routes': ['resources'] },
	{ 'name': 'document_permissions', 'routes': ['documents.manage', 'documents.published'] },
];

const permissions = ref(null) //Permissions objects loaded from api response based on permissions_list
const authStore = useAuthStore()
const tenantSettings = ref(null)
const isSaving = ref(false)//Saving state
const isLoading = ref(false)//Loading state
const nav_to = ref(null)//Route to navigate to when a menu item is clicked
const role = ref({})//Full role object
const role_init = ref(null)//Initial role object for comparison
const toaster = new Toaster()
const showDiscardChangesModal = ref(false);
const channel = useBroadcastChannel({ name: 'NavigationCancel' })

/**
 * Intercepts navigation via a side menu to check for changes.
 * Uses a broadcast channel to communicate with the navigation menu to keep Admin Center from closing.
 */
onBeforeRouteLeave((to, from) =>
{
	if (checkChanges())
	{
		nav_to.value = to
		toggleDiscardChangesModal(true)
		channel.post({'route_to': to.name, 'route_from': from.name})
		return false
	}
})

/**
 * Loads permissions for the role edit page.
 */
onMounted(() =>
{
	isLoading.value = true
	if (history?.state?.role_id && history.state.role_id > 0)
	{
		organizationRolesApi.getRole(history.state.role_id, onGetRole)
	}
	else
	{
		router.push({ name: 'admin.organizations.roles' })
	}
})

onUnmounted(() =>
{
	channel.close()
})

/**
 * Handles the response from the API call to get the role.
 * @param p_response
 * @param p_error_eh
 */
function onGetRole(p_response, p_error_eh)
{
	try
	{
		isLoading.value = false
		if (p_error_eh)
		{
			p_response.showErrorBanner()
		}
		else
		{
			const data = p_response.getData();
			tenantSettings.value = authStore.getAuth?.tenantSettings()
			const modules = tenantSettings.value?.modules.value
			if (!tenantSettings.value || !data || data.length === 0)
			{
				toaster.error("Failed to load roles. Please try again later.")
				return
			}

			role.value = p_response.getData()[0]
			role_init.value = JSON.parse(JSON.stringify(role.value))

			permissions.value = {};
			for (const index in permission_list)
			{
				const permission = permission_list[index];
				const key = permission.name

				const has_route = key === 'admin_permissions' || modules.findIndex((p_item) => permission.routes.indexOf(p_item.route) >= 0 && p_item.enabled) >= 0

				if (has_route && key !== 'resource_permissions')
				{
					permissions.value[key] = { 'id': key, 'permissions': role.value[key] };
				}
			}

		}
	}
	catch (e)
	{
		if (authStore.isDeveloper) console.error(e)
	}
}

/**
 * Saves the role changes.
 */
function save()
{
	isSaving.value = true;
	organizationRolesApi.saveRole({"role": JSON.stringify(role.value)}, onSave)
}

/**
 * Handles the response from the API call to save the role.
 * @param p_response
 * @param p_error_eh
 */
function onSave(p_response, p_error_eh)
{
	try
	{
		if (p_error_eh)
		{
			p_response.showErrorBanner()
		}
		else
		{
			toaster.success("Role updated successfully!")
			setTimeout(() => {
				isSaving.value = false;
				role_init.value = JSON.parse(JSON.stringify(role.value))
				router.back()
			}, 1000)
		}
	}
	catch (e)
	{
		if (authStore.isDeveloper) console.error(e)
	}
}

/**
 * Show or Hide the Discard Changes modal
 * @param {boolean} p_show_eh True to show, false to hide
 */
function toggleDiscardChangesModal(p_show_eh)
{
	showDiscardChangesModal.value = p_show_eh
}

/**
 * Checks if there are any changes to the role.
 * @returns {boolean}
 */
function checkChanges()
{
	return JSON.stringify(role.value) !== JSON.stringify(role_init.value)
}

/**
 * Cancels the role edit and returns to the previous page or novgiates to the page clicked in the navigation menu.
 * @param p_check_changes
 */
function cancel(p_check_changes)
{
	if (p_check_changes)
	{
		if (checkChanges())
		{
			toggleDiscardChangesModal(true)
		}
		else
		{
			router.back()
		}
	}
	else
	{
		role_init.value = JSON.parse(JSON.stringify(role.value))
		if (nav_to.value)
		{
			router.push(nav_to.value)
		}
		else
		{
			router.back()
		}
	}
}

/**
 * Changes the label of the cancel button based on if the page has been modified.
 * @type {ComputedRef<string>}
 */
const cancelLabel = computed(() => {
	if (authStore.isPortalAdmin)
	{
		return checkChanges() ? "Cancel" : "Back"
	}
	return "Back"
})

</script>

<template>
	<AdminLayout>
		<LoadingSpinner v-if="isLoading" />
		<div v-if="!isLoading">
			<div class="flex flex-col flex-col-reverse sm:flex-row">
				<div class="-ml-3 group w-100">
					<div v-if="authStore.isPortalAdmin" class="flex flex-row h-10">
						<div class="group-hover:hidden group-focus-within:hidden font-semibold text-xl pl-3 pr-1  pt-[6px] ">{{ role.role }}</div>
						<div class="group-hover:hidden group-focus-within:hidden mt-1 pt-2">
							<svg xmlns="http://www.w3.org/2000/svg" class="fill-current group-hover:text-primary-hover" width="16" height="16" viewBox="0 0 20 20">
								<title>Edit role name</title>
								<path fill-rule="evenodd" clip-rule="evenodd" d="M1.56622 3.23223C2.03506 2.76339 2.67094 2.5 3.33398 2.5H9.16732C9.62755 2.5 10.0006 2.8731 10.0006 3.33333C10.0006 3.79357 9.62755 4.16667 9.16732 4.16667H3.33398C3.11297 4.16667 2.90101 4.25446 2.74473 4.41074C2.58845 4.56702 2.50065 4.77899 2.50065 5V16.6667C2.50065 16.8877 2.58845 17.0996 2.74473 17.2559C2.90101 17.4122 3.11297 17.5 3.33398 17.5H15.0006C15.2217 17.5 15.4336 17.4122 15.5899 17.2559C15.7462 17.0996 15.834 16.8877 15.834 16.6667V10.8333C15.834 10.3731 16.2071 10 16.6673 10C17.1276 10 17.5006 10.3731 17.5006 10.8333V16.6667C17.5006 17.3297 17.2373 17.9656 16.7684 18.4344C16.2996 18.9033 15.6637 19.1667 15.0006 19.1667H3.33398C2.67094 19.1667 2.03506 18.9033 1.56622 18.4344C1.09738 17.9656 0.833984 17.3297 0.833984 16.6667V5C0.833984 4.33696 1.09738 3.70107 1.56622 3.23223Z"/>
								<path fill-rule="evenodd" clip-rule="evenodd" d="M16.6673 2.39909C16.4195 2.39909 16.1818 2.49754 16.0066 2.67278L8.25314 10.4262L7.81264 12.1882L9.57463 11.7477L17.3281 3.99427C17.5033 3.81903 17.6018 3.58135 17.6018 3.33352C17.6018 3.0857 17.5033 2.84802 17.3281 2.67278C17.1528 2.49754 16.9152 2.39909 16.6673 2.39909ZM14.8281 1.49427C15.3159 1.00647 15.9775 0.732422 16.6673 0.732422C17.3572 0.732422 18.0188 1.00647 18.5066 1.49427C18.9944 1.98207 19.2684 2.64367 19.2684 3.33352C19.2684 4.02338 18.9944 4.68498 18.5066 5.17278L10.5899 13.0894C10.4831 13.1962 10.3493 13.272 10.2028 13.3086L6.86945 14.142C6.58547 14.213 6.28506 14.1298 6.07808 13.9228C5.8711 13.7158 5.78789 13.4154 5.85888 13.1314L6.69222 9.79808C6.72885 9.65155 6.80461 9.51773 6.91141 9.41093L14.8281 1.49427Z"/>
							</svg>
						</div>
						<div class="hidden group-hover:flex group-focus-within:flex -ml-[5px]">
							<InputText v-model="role.role" type="text" class="w-100 !mb-1" input-height="h-10" :icon="InputIcon.EDIT" custom-class="font-semibold text-xl"></InputText>
						</div>
					</div>
					<div v-else class="font-semibold text-xl pl-3 pr-1">{{ role.role }}</div>
				</div>
				<div class="flex-1"></div>
				<div class="flex flex-row justify-center">
					<SubmitButton :label="cancelLabel" :type="ButtonType.Outline" :size="ButtonSize.ExtraSmall" :disabled="isSaving" :loading="isSaving" class="mr-3" @click.prevent="cancel(true)" />
					<SubmitButton v-if="authStore.isPortalAdmin" label="Save" :size="ButtonSize.ExtraSmall" @click="save()" :disabled="cancelLabel !== 'Cancel'" :loading="isSaving" />
				</div>
			</div>
			<div class="text-sm text-grayscale-body mt-2 mb-5">
				{{ role.description }}
			</div>

			<hr>

			<!-- resource_permissions are excluded in onGetRole b/c they are not used yet -->
			<div class="flex flex-row flex-wrap justify-left mt-5 gap-x-8 ">
				<OrganizationRoleEditCard v-for="perm in permissions" :key="perm.id" :perm="perm.permissions" :disabled="!authStore.isPortalAdmin"  ></OrganizationRoleEditCard>
			</div>
		</div>

		<Teleport to="html">
			<MultiButtonModal
				:show="showDiscardChangesModal"
				icon="info"
				title="Discard Changes"
				message="Are you sure?"
				subMessage="Leaving now will discard any changes made."
				:buttons="[
									new ModalButtonData(
										'Cancel', () => { toggleDiscardChangesModal(false); }, ButtonType.Outline, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { cancel(false) }, ButtonType.Default, ButtonSize.Small
									)
								]"
			/>
		</Teleport>

	</AdminLayout>
</template>

<style scoped>

</style>